import type { Distance } from "@pdfking/math"
import { Ast, MM } from "@pdfking/quantity"

import type { GrowContentMeta, GrowLayout } from "#public"
import {
  Across,
  Along,
  Content,
  DIM_CONST,
  DIM_HEIGHT,
  DIM_WIDTH,
  Fold,
  PAGE_ITSELF,
  Pages,
  RecordByPrimaryKey,
  makeFoldEntry,
  GrowContentKind,
} from "#public"

// A panel of the fold, a poster as wide as the page or `narrower` by `less`
// so it folds inside, with the pages it shows front and rear.
const panel = (
  front: number,
  rear: number,
  opts: Partial<GrowContentMeta> = {},
  less: Distance | undefined = undefined
): GrowLayout =>
  Content(
    GrowContentKind.PAGE,
    less ? Ast.Sub(DIM_WIDTH, DIM_CONST(less)) : DIM_WIDTH,
    Pages(front, rear, opts)
  )

// A panel laid upside down, on the lower row of a cross fold.
const turned = (front: number, rear: number, less: Distance | undefined = undefined): GrowLayout =>
  panel(front, rear, { rotation: 180 }, less)

// The crease between two panels: the turn it makes folded, rear onto rear
// when positive, and the step it opens at from the closed fold, 1 first.
type Crease = [angle: number, step: number]

// A leaflet's creases never lie quite flat.
const AJAR = 0.35
const crease = ([angle, step]: Crease): GrowLayout => Fold(angle, step, AJAR)

// Panels in a row along the opening, as high as the page, a crease between
// each two.
const row = (panels: GrowLayout[], creases: Crease[]): GrowLayout =>
  Along(
    panels.flatMap((p, i) => (i ? [crease(creases[i - 1]!), p] : [p])),
    DIM_HEIGHT
  )

// Rows of panels from the top down, each creased as `creases`, `across`
// between the rows: a cross fold.
const rows = (lines: GrowLayout[][], creases: Crease[], across: Crease): GrowLayout =>
  Across(
    lines.flatMap((line, i) => (i ? [crease(across), row(line, creases)] : [row(line, creases)]))
  )

const MM2 = MM(2)

export const FoldGrowList = RecordByPrimaryKey([
  // The page as it is: one poster, pages 1 and 2.
  makeFoldEntry("b", "NO_FOLD", PAGE_ITSELF),

  makeFoldEntry("b2", "HALF_FOLD_4_PAGES", row([panel(4, 3), panel(1, 2)], [[180, 1]]), {
    maximum_tickness: MM(0.36),
    depth_molt: 2,
    sides_tuple: [4],
  }),

  makeFoldEntry(
    "b3",
    "Z_FOLD_6_PAGES",
    row(
      [panel(5, 4), panel(6, 3), panel(1, 2)],
      [
        [-180, 1],
        [180, 1],
      ]
    ),
    {
      maximum_tickness: MM(0.36),
      depth_molt: 3,
      sides_tuple: [6],
    }
  ),

  makeFoldEntry(
    "b3w",
    "C_FOLD_6_PAGES",
    row(
      [panel(5, 4, {}, MM2), panel(6, 3), panel(1, 2)],
      [
        [180, 2],
        [180, 1],
      ]
    ),
    {
      maximum_tickness: MM(0.36),
      depth_molt: 3,
      sides_tuple: [6],
    }
  ),

  makeFoldEntry(
    "b3z",
    "CROSS_FOLD_C_FOLD_12_PAGES",
    rows(
      [
        [panel(5, 12, {}, MM2), panel(6, 11), panel(1, 10)],
        [turned(9, 4, MM2), turned(8, 3), turned(7, 2)],
      ],
      [
        [180, 2],
        [180, 1],
      ],
      [180, 3]
    ),
    { maximum_tickness: MM(0.17), depth_molt: 3, sides_tuple: [12] }
  ),

  makeFoldEntry(
    "b4",
    "W_FOLD_8_PAGES",
    row(
      [panel(6, 5), panel(7, 4), panel(8, 3), panel(1, 2)],
      [
        [180, 2],
        [-180, 1],
        [180, 1],
      ]
    ),
    {
      maximum_tickness: MM(0.36),
      depth_molt: 4,
      sides_tuple: [8],
    }
  ),

  makeFoldEntry(
    "b4w",
    "PARALLEL_FOLD_8_PAGES",
    row(
      [panel(8, 7), panel(1, 6), panel(2, 5, {}, MM2), panel(3, 4, {}, MM2)],
      [
        [180, 1],
        [180, 2],
        [-180, 1],
      ]
    ),
    { maximum_tickness: MM(0.22), depth_molt: 4, sides_tuple: [8] }
  ),

  makeFoldEntry(
    "b4ww",
    "DOUBLE_GATE_FOLD_8_PAGES",
    row(
      [panel(3, 7, {}, MM2), panel(8, 6), panel(1, 5), panel(2, 4, {}, MM2)],
      [
        [180, 2],
        [180, 1],
        [180, 2],
      ]
    ),
    { maximum_tickness: MM(0.36), depth_molt: 4, sides_tuple: [8] }
  ),

  makeFoldEntry(
    "b5",
    "ACCORDION_FOLD_10_PAGES",
    row(
      [panel(7, 6), panel(8, 5), panel(9, 4), panel(10, 3), panel(1, 2)],
      [
        [-180, 3],
        [180, 2],
        [-180, 1],
        [180, 1],
      ]
    ),
    { maximum_tickness: MM(0.3), depth_molt: 5, sides_tuple: [10] }
  ),

  makeFoldEntry(
    "b6",
    "ACCORDION_FOLD_12_PAGES",
    row(
      [panel(8, 7), panel(9, 6), panel(10, 5), panel(11, 4), panel(12, 3), panel(1, 2)],
      [
        [180, 4],
        [-180, 3],
        [180, 2],
        [-180, 1],
        [180, 1],
      ]
    ),
    { maximum_tickness: MM(0.3), depth_molt: 6, sides_tuple: [12] }
  ),

  makeFoldEntry(
    "bc",
    "CROSS_FOLD_8_PAGES",
    rows(
      [
        [panel(8, 7), panel(1, 6)],
        [turned(3, 5), turned(2, 4)],
      ],
      [[180, 1]],
      [180, 2]
    ),
    { maximum_tickness: MM(0.22), depth_molt: 4, sides_tuple: [16] }
  ),
] as const)
